import React, { useState, useRef, useCallback } from 'react';

export interface TagsInputProps {
    value: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    allowDuplicates?: boolean;
    maxTags?: number;
}

// Lightweight React implementation mimicking bootstrap-tagsinput behavior/styles already present in legacy CSS.
export const TagsInput: React.FC<TagsInputProps> = ({
    value,
    onChange,
    placeholder = 'Add a tag',
    disabled = false,
    allowDuplicates = false,
    maxTags
}) => {
    const [input, setInput] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    const normalized = value || [];

    const focusInput = useCallback(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    const addTag = useCallback((raw: string) => {
        const tag = raw.trim();
        if (!tag) return;
        if (maxTags && normalized.length >= maxTags) return;
        if (!allowDuplicates && normalized.some(t => t.toLowerCase() === tag.toLowerCase())) {
            setInput('');
            return;
        }
        onChange([...normalized, tag]);
        setInput('');
    }, [normalized, onChange, allowDuplicates, maxTags]);

    const removeTag = useCallback((idx: number) => {
        const next = normalized.filter((_, i) => i !== idx);
        onChange(next);
    }, [normalized, onChange]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        if ([',', ';'].includes(e.key)) {
            e.preventDefault();
            addTag(input);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            addTag(input);
        } else if (e.key === 'Backspace' && input === '' && normalized.length > 0) {
            // Remove last tag
            removeTag(normalized.length - 1);
        }
    };

    const handleBlur = () => {
        // Add pending input on blur
        addTag(input);
    };

    return (
        <div className={`bootstrap-tagsinput form-control`} style={{ display: 'flex', flexWrap: 'wrap', cursor: disabled ? 'not-allowed' : 'text' }} onClick={focusInput}>
            {normalized.map((t, i) => (
                <span key={i} className="tag label label-info" style={{ marginRight: 4, display: 'flex', alignItems: 'center' }}>
                    {t}
                    {!disabled && (
                        <span
                            data-role="remove"
                            onClick={(e) => { e.stopPropagation(); removeTag(i); }}
                            style={{ marginLeft: 4, cursor: 'pointer' }}
                            aria-label={`Remove tag ${t}`}
                            role="button"
                        />
                    )}
                </span>
            ))}
            <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                placeholder={normalized.length === 0 ? placeholder : ''}
                disabled={disabled}
                style={{ flex: 1, minWidth: 120, border: 'none', outline: 'none', boxShadow: 'none' }}
            />
        </div>
    );
};

export default TagsInput;
